import React from "react";
import { max } from "d3-array";
import { scaleLinear } from "d3-scale";
import { useSpring, useSpringRef, useChain, animated } from "@react-spring/web";

import { Group } from "./Group";
import { Header } from "./Header";
import { InfoSymbol } from "./InfoSymbol";
import { Footer } from "./Footer";

interface BarChartDatum {
  key: string;
  value: number;
  color?: string;
}

interface BarChartProps {
  data: BarChartDatum[];
  title?: string;
  description?: string;
  source?: string;
  sourceLink?: string;
  infoLink?: string;
  unit?: string;
  width?: number;
  height?: number;
  isAnimated?: boolean;
}

export const BarChart: React.FC<BarChartProps> = ({
  data,
  title,
  description,
  source,
  sourceLink,
  infoLink,
  unit = "",
  width = 210,
  height = 360,
  isAnimated = true,
}) => {
  const margin = { top: 110, right: 70, bottom: 0, left: 0 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  const xMax = max(data, (d) => d.value)!;
  const yMax = data.length;
  const x = scaleLinear().domain([0, xMax]).range([0, innerWidth]);
  const y = scaleLinear().domain([0, yMax]).range([0, innerHeight]);

  const animatedData = data.map((datum) => {
    const barRef = useSpringRef();
    const barAnimation = useSpring({
      config: {
        duration: isAnimated ? 750 : 0,
      },
      width: isAnimated ? x(datum.value) : 0,
      ref: barRef,
    });

    const labelRef = useSpringRef();
    const labelAnimation = useSpring({
      config: {
        duration: isAnimated ? 250 : 0,
      },
      opacity: isAnimated ? 1 : 0,
      ref: labelRef,
    });

    useChain([barRef, labelRef], [0, 0.75]);

    return {
      ...datum,
      barAnimation,
      labelAnimation,
    };
  });

  return (
    <>
      {title && <Header title={title} description={description} />}
      {infoLink && (
        <InfoSymbol
          link={infoLink}
          transform={`translate(${width - 15}, ${-5})`}
        />
      )}
      {source && (
        <Footer
          text={`Quelle: ${source}`}
          link={sourceLink}
          alignRight={true}
          transform={`translate(${width}, ${height - 5})`}
        />
      )}
      {animatedData.map((datum, index) => (
        <Group
          key={index}
          transform={`translate(${margin.left}, ${margin.top + y(index)})`}
        >
          <text
            fontFamily="TheMixExtraBold, sans-serif"
            fontWeight="700"
            fontSize="14"
            dy="-0.5em"
          >
            {datum.key}
          </text>
          <animated.rect
            {...datum.barAnimation}
            x="0"
            y="0"
            height="15"
            fill={datum.color}
          />
          <animated.text
            {...datum.labelAnimation}
            // fontFamily="TheMixExtraBold, sans-serif"
            // fontWeight="700"
            fontSize="14"
            x={x(datum.value)}
            dy="0.9em"
            dx="0.5em"
            fill={datum.color}
          >
            {datum.value} {unit}
          </animated.text>
          {datum.key == "Unzureichend" && (
            <Group transform={`translate(${100}, ${-15})`}>
              <animated.path
                {...datum.labelAnimation}
                x="0"
                y="0"
                d="M2.696 7.028 2.634 4.23a.5.5 0 0 0-.999.022l.098 4.492a.501.501 0 0 0 .637.47l4.021-1.147a.5.5 0 0 0-.275-.962l-2.752.786c1.21-2.352 3.29-4.112 5.543-4.825 3.911-1.237 7.944-.682 7.944-.682a.5.5 0 0 0 .121-.993s-4.251-.58-8.367.722c-2.365.748-4.537 2.56-5.909 4.915Z"
                transform="rotate(-15) scale(1.2)"
                strokeLinejoin="round"
                fill="#a9aabc"
              />
              <animated.text
                {...datum.labelAnimation}
                x="24"
                y="-18"
                fontSize="12"
                fill="#a9aabc"
                dy="19"
              >
                Deutschland
              </animated.text>
            </Group>
          )}
        </Group>
      ))}
    </>
  );
};
